import { useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { IconButton } from '../ui/button';
import { Field, Input } from '../ui/input';

export interface ChipEingabeProps {
  label: ReactNode;
  /** Kurzer Hinweis unter dem Feld. */
  hint?: ReactNode;
  /** Die Wörter, in der Reihenfolge der Eingabe. */
  werte: string[];
  onChange: (werte: string[]) => void;
}

/** Wörter aus einer Eingabe: an Kommas geteilt, ohne Leeres und ohne Doppelte. */
export function chipWoerter(eingabe: string, vorhanden: string[]): string[] {
  const neue: string[] = [];
  for (const roh of eingabe.split(',')) {
    const wort = roh.trim();
    if (wort && !vorhanden.includes(wort) && !neue.includes(wort)) neue.push(wort);
  }
  return neue;
}

/**
 * Eine Liste aus Wörtern als Chips, etwa Regionen, Themen und Ausschlüsse in
 * den Radar-Einstellungen der Finanzen. Enter oder Komma fügt hinzu, Verlassen
 * des Felds übernimmt, was dasteht; eingefügter Text mit Kommas wird
 * aufgeteilt. Das x am Chip entfernt ihn, Rücktaste im leeren Feld den
 * letzten. Chips ohne Punkt: Es sind Wörter, kein Status.
 */
export function ChipEingabe({ label, hint, werte, onChange }: ChipEingabeProps) {
  const [eingabe, setEingabe] = useState('');

  const uebernehmen = (text: string) => {
    const neue = chipWoerter(text, werte);
    if (neue.length) onChange([...werte, ...neue]);
    setEingabe('');
  };

  const taste = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      uebernehmen(eingabe);
    } else if (event.key === 'Backspace' && !eingabe && werte.length) {
      onChange(werte.slice(0, -1));
    }
  };

  // Eingefügtes mit Komma sofort aufteilen; ohne Komma normal einfügen.
  const einfuegen = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!text.includes(',')) return;
    event.preventDefault();
    uebernehmen(eingabe + text);
  };

  return (
    <div className="flex flex-col gap-2">
      <Field label={label} hint={hint}>
        {(f) => (
          <Input
            {...f}
            value={eingabe}
            placeholder="Eingabe, dann Enter"
            enterKeyHint="enter"
            onChange={(e) => setEingabe(e.target.value)}
            onKeyDown={taste}
            onPaste={einfuegen}
            onBlur={() => uebernehmen(eingabe)}
          />
        )}
      </Field>
      {werte.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={typeof label === 'string' ? label : undefined}>
          {werte.map((wert) => (
            <li
              key={wert}
              className="inline-flex h-7 items-center gap-0.5 rounded-full bg-muted pl-2.5 pr-0.5 text-xs font-medium text-foreground touch:h-9"
            >
              {wert}
              <IconButton
                icon={X}
                label={`„${wert}“ entfernen`}
                variant="ghost"
                size="sm"
                noTooltip
                className="size-6 rounded-full touch:size-8"
                onClick={() => onChange(werte.filter((w) => w !== wert))}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
